import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, createTheme, Divider, Tab, Tabs, ThemeProvider, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { useMeetingHubContext } from '../MeetingHubContext';
import { useMeetingStaffContext } from './MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import { useHubEvent } from 'hooks/useHubEvent';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import AttendanceSection from '../AttendanceSection';
import ConferenceInfoSection from '../ConferenceInfoSection';
import MotionSetupPanel from './MotionSetupPanel';
import BallotReviewPanel from './BallotReviewPanel';
import MeetingSessionPanel from '../MeetingSessionPanel';
import ActivityLogViewCard from '../ActivityLogModal/ActivityLogViewCard';
import { appTheme } from 'util/layout';
import { activityLogQueryParameter, defaultMotions, previewQueryParameter } from 'util/meeting';
import { ActivityType } from 'store/enums/BallotReviewTypes';
import MeetingHubEvents from 'store/enums/MeetingHubActions';

import commonStyles from 'styles/common.module.scss';

enum StaffViewTab {
  Vote = '1',
  BallotReview = '2',
}

const MeetingStaffView: React.FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const showPreview = searchParams.get(previewQueryParameter);
  const showActivityLog = searchParams.get(activityLogQueryParameter);

  const { meetingId, activityLogs, connection, refetchAttendees, refetchMeetingSession, refetchActivityLogs } =
    useMeetingHubContext();
  const {
    resetSession,
    motions,
    firstLoadingMotions,
    refetchMotions,
    refetchDocuments,
    refetchRejects,
    refetchTechnicalIssues,
    refetchComments,
  } = useMeetingStaffContext();
  const displayLogs = useMemo(
    () => (activityLogs ?? []).filter((log) => log.type !== ActivityType.Connection),
    [activityLogs]
  );
  const [activeTab, setActiveTab] = useState<string>(StaffViewTab.Vote);

  const localTheme = createTheme({
    ...appTheme,
    components: {
      ...appTheme.components,
      MuiTextField: {
        defaultProps: {
          size: 'small',
          fullWidth: true,
        },
      },
      MuiRadio: {
        defaultProps: {
          size: 'small',
        },
      },
    },
  });

  const handleTabChange = useCallback(
    (_: any, value = StaffViewTab.Vote) => {
      setActiveTab(value);
      resetSession();
    },
    [resetSession]
  );

  const handleOpenPreview = useCallback(() => {
    const { origin, pathname, hash } = window.location;

    const url = new URL(`${origin}${pathname}${hash}`);
    url.searchParams.set(previewQueryParameter, 'true');

    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    if (!motions?.length && !firstLoadingMotions) {
      MeetingService.saveMotions(meetingId, defaultMotions);
      refetchMotions?.();
    }
  }, [firstLoadingMotions, meetingId, motions, refetchMotions]);

  useHubEvent(connection, MeetingHubEvents.SessionUpdated, refetchMeetingSession);
  useHubEvent(connection, MeetingHubEvents.AttendeeUpdated, refetchAttendees);
  useHubEvent(connection, MeetingHubEvents.AttendeeRemoved, refetchAttendees);
  useHubEvent(connection, MeetingHubEvents.MotionUpdated, refetchMotions);
  useHubEvent(connection, MeetingHubEvents.DocumentUpdated, refetchDocuments);
  useHubEvent(connection, MeetingHubEvents.RejectUpdated, refetchRejects);
  useHubEvent(connection, MeetingHubEvents.TechnicalIssueUpdated, refetchTechnicalIssues);
  useHubEvent(connection, MeetingHubEvents.CommentUpdated, refetchComments);
  useHubEvent(connection, MeetingHubEvents.ActivityLogAdded, refetchActivityLogs);
  useHubEvent(connection, MeetingHubEvents.ActivityLogRemoved, refetchActivityLogs);

  return (
    <ThemeProvider theme={localTheme}>
      {showPreview === 'true' ? (
        <>
          <Typography variant="h6" className="mb-4">
            Session Preview:
          </Typography>
          <MeetingSessionPanel />
        </>
      ) : showActivityLog === 'true' ? (
        <GridContainer>
          {displayLogs.map((log) => (
            <GridItem key={log.id}>
              <ActivityLogViewCard activityLog={log} />
            </GridItem>
          ))}
        </GridContainer>
      ) : (
        <GridContainer>
          <GridItem>
            <ConferenceInfoSection />
          </GridItem>
          <GridItem>
            <AttendanceSection />
          </GridItem>
          <GridItem>
            <TabContext value={activeTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  indicatorColor={'primary'}
                  textColor={'primary'}
                  variant={'scrollable'}
                >
                  <Tab value={StaffViewTab.Vote} label={'Vote'} className={commonStyles.tab} />
                  <Tab value={StaffViewTab.BallotReview} label={'Ballot Review'} className={commonStyles.tab} />
                </Tabs>
              </Box>
              <TabPanel className={commonStyles.tabPanel} value={StaffViewTab.Vote}>
                <MotionSetupPanel />
              </TabPanel>
              <TabPanel className={commonStyles.tabPanel} value={StaffViewTab.BallotReview}>
                <BallotReviewPanel />
              </TabPanel>
            </TabContext>
          </GridItem>
          <GridItem sx={{ p: 0 }}>
            <Divider />
          </GridItem>
          <GridItem>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" className="mb-4">
                Session Preview:
              </Typography>
              <div>
                <Button variant="contained" color="primary" onClick={handleOpenPreview}>
                  Pop-out preview
                </Button>
              </div>
            </Box>
            <MeetingSessionPanel />
          </GridItem>
        </GridContainer>
      )}
    </ThemeProvider>
  );
};

export default MeetingStaffView;
